import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useCommentStore } from "src/stores/commentStore";

const escapeCssAttributeValue = (value) => String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');

/**
 * Custom hook for managing annotation navigation in the Chapter Editor
 *
 * This hook handles all annotation-related functionality including:
 * - Tracking the current annotation based on viewport position
 * - Providing navigation functions to jump between annotations
 * - Caching annotation elements for performance optimization
 * - Handling scroll-to-annotation with sticky header awareness
 * - Managing annotation cache rebuilds during loading states
 *
 * @param {Object} params - Hook parameters
 * @param {React.RefObject} params.centerColumnRef - Ref to the scrollable center column
 * @param {React.RefObject} params.editorToolbarRef - Ref to the editor toolbar for sticky calculations
 * @param {React.RefObject} params.editorRef - Ref to the main editor container
 * @param {boolean} params.isLoading - Whether feedback generation is currently loading
 * @param {boolean} params.isSticky - Whether the feedback panel is in sticky mode
 *
 * @returns {Object} Navigation state and functions
 * @returns {Object} returns.currentAnnotation - Current annotation info {number, total}
 * @returns {Function} returns.goToCurrentComment - Scroll to current annotation
 * @returns {Function} returns.goToNextComment - Navigate to next annotation
 * @returns {Function} returns.goToPrevComment - Navigate to previous annotation
 * @returns {Function} returns.scrollToAnnotation - Scroll to specific annotation by index
 */
export const useAnnotationNavigation = ({
  centerColumnRef,
  editorToolbarRef,
  editorRef,
  isLoading,
  isSticky
}) => {
  const [currentAnnotation, setCurrentAnnotation] = useState({ number: null, total: 0 });
  const comments = useCommentStore((state) => state.comments);
  const DOMDocument = useCommentStore((state) => state.DOMDocument);
  const commentByAnnotationId = useMemo(() => {
    const map = new Map();
    comments.forEach((comment) => {
      const annotationId = String(comment?.annotationId || "").trim();
      if (annotationId) {
        map.set(annotationId, comment);
      }
    });
    return map;
  }, [comments]);

  // Performance optimization: cache annotation data
  const annotationCacheRef = useRef({ elements: [], lastUpdate: 0 });
  const iframeElementRef = useRef(null);
  const isRebuildingRef = useRef(false);

  // Function to scroll to a specific annotation
  const scrollToAnnotation = useCallback(
    (annotationIndex, position = "top") => {
      if (!DOMDocument || !centerColumnRef?.current || annotationIndex < 0) return;

      try {
        // Use cached annotations if available
        let annotationsWithPositions = annotationCacheRef.current.elements;

        if (annotationsWithPositions.length === 0) {
          // Fallback: rebuild cache
          const annotationElements = Array.from((DOMDocument.getElementById?.("editorContainer") || DOMDocument).querySelectorAll("span[data-annotation-id]"));

          // Group elements by annotation ID to handle cases where one annotation spans multiple DOM elements
          const annotationGroups = new Map();

          annotationElements.forEach((element) => {
            const annotationIdAttr = element.getAttribute("data-annotation-id");
            if (!annotationIdAttr) return;

            // An element can have multiple annotation IDs separated by spaces
            const annotationIds = annotationIdAttr.split(" ");

            annotationIds.forEach((annotationId) => {
              if (!annotationId.trim()) return;

              const comment = commentByAnnotationId.get(annotationId.trim());
              if (!comment) return;

              // Only store the first element for each annotation ID
              if (!annotationGroups.has(annotationId)) {
                annotationGroups.set(annotationId, { element, comment, annotationId });
              }
            });
          });

          // Convert to array and sort by position
          annotationsWithPositions = Array.from(annotationGroups.values())
            .sort((a, b) => {
              const aRect = a.element.getBoundingClientRect();
              const bRect = b.element.getBoundingClientRect();
              return aRect.top - bRect.top;
            });
        }

        if (annotationIndex >= annotationsWithPositions.length) return;

        const targetAnnotation = annotationsWithPositions[annotationIndex];
        const targetRect = targetAnnotation.element.getBoundingClientRect();

        // Use cached iframe element
        const iframeElement = iframeElementRef.current || window.document.getElementById("editorContainer");
        if (!iframeElement) return;

        const centerColumnRect = centerColumnRef.current.getBoundingClientRect();

        // Calculate sticky header offset
        const stickyHeaderOffset =
          isSticky && editorToolbarRef?.current
            ? editorToolbarRef.current.getBoundingClientRect().height + 85 // Add 10px padding
            : 20; // Default offset when not sticky

        // Calculate target scroll position
        let targetScrollTop;

        if (position === "top") {
          const annotationTopInMainDoc = targetRect.top;
          const offsetFromCenterTop = annotationTopInMainDoc - centerColumnRect.top;
          targetScrollTop = centerColumnRef.current.scrollTop + offsetFromCenterTop - stickyHeaderOffset;
        } else {
          const annotationMiddleInMainDoc = targetRect.top + targetRect.height / 2;
          const centerColumnMiddle = centerColumnRect.top + centerColumnRect.height / 2;
          const offsetFromCenter = annotationMiddleInMainDoc - centerColumnMiddle;
          targetScrollTop = centerColumnRef.current.scrollTop + offsetFromCenter;
        }

        // Smooth scroll to target
        centerColumnRef.current.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: "smooth",
        });
      } catch (error) {
        console.error("Error scrolling to annotation:", error);
      }
    },
    [DOMDocument, centerColumnRef, commentByAnnotationId, isSticky, editorToolbarRef]
  );

  // Scroll to an annotation by its ID directly (no index lookup needed)
  const scrollToAnnotationById = useCallback(
    (annotationId, position = "top", options = {}) => {
      if (!DOMDocument || !centerColumnRef?.current) return;

      try {
        const selector = `span[data-annotation-id~="${escapeCssAttributeValue(annotationId)}"]`;
        const queryRoot = DOMDocument.getElementById?.("editorContainer") || DOMDocument;
        const element = queryRoot.querySelector(selector);
        if (!element) return;

        const iframeElement = iframeElementRef.current || window.document.getElementById("editorContainer");
        if (!iframeElement) return;

        const targetRect = element.getBoundingClientRect();
        const centerColumnRect = centerColumnRef.current.getBoundingClientRect();

        const stickyHeaderOffset =
          isSticky && editorToolbarRef?.current
            ? editorToolbarRef.current.getBoundingClientRect().height + 85
            : 20;

        let targetScrollTop;

        if (position === "top") {
          const annotationTopInMainDoc = targetRect.top;
          const offsetFromCenterTop = annotationTopInMainDoc - centerColumnRect.top;
          targetScrollTop = centerColumnRef.current.scrollTop + offsetFromCenterTop - stickyHeaderOffset;
        } else {
          const annotationMiddleInMainDoc = targetRect.top + targetRect.height / 2;
          const centerColumnMiddle = centerColumnRect.top + centerColumnRect.height / 2;
          const offsetFromCenter = annotationMiddleInMainDoc - centerColumnMiddle;
          targetScrollTop = centerColumnRef.current.scrollTop + offsetFromCenter;
        }

        centerColumnRef.current.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: options.behavior ?? "smooth",
        });
      } catch (error) {
        console.error("Error scrolling to annotation:", error);
      }
    },
    [DOMDocument, centerColumnRef, isSticky, editorToolbarRef]
  );

  // Navigation functions
  const goToCurrentComment = useCallback(() => {
    if (currentAnnotation.number) {
      scrollToAnnotation(currentAnnotation.number - 1, "top");
    }
  }, [currentAnnotation.number, scrollToAnnotation]);

  const goToNextComment = useCallback(() => {
    if (currentAnnotation.number && currentAnnotation.number < currentAnnotation.total) {
      scrollToAnnotation(currentAnnotation.number, "top");
    }
  }, [currentAnnotation.number, currentAnnotation.total, scrollToAnnotation]);

  const goToPrevComment = useCallback(() => {
    if (currentAnnotation.number && currentAnnotation.number > 1) {
      scrollToAnnotation(currentAnnotation.number - 2, "top");
    }
  }, [currentAnnotation.number, scrollToAnnotation]);

  // Optimized function to rebuild annotation cache
  const rebuildAnnotationCache = useCallback(() => {
    if (!DOMDocument || commentByAnnotationId.size === 0) {
      annotationCacheRef.current = { elements: [], lastUpdate: Date.now() };
      return;
    }

    try {
      const annotationElements = Array.from((DOMDocument.getElementById?.("editorContainer") || DOMDocument).querySelectorAll("span[data-annotation-id]"));

      // Group elements by annotation ID to handle cases where one annotation spans multiple DOM elements
      const annotationGroups = new Map();

      annotationElements.forEach((element) => {
        const annotationIdAttr = element.getAttribute("data-annotation-id");
        if (!annotationIdAttr) return;

        // An element can have multiple annotation IDs separated by spaces
        const annotationIds = annotationIdAttr.split(" ");

        annotationIds.forEach((annotationId) => {
          if (!annotationId.trim()) return;

          const comment = commentByAnnotationId.get(annotationId.trim());
          if (!comment) return;

          // Only store the first element for each annotation ID
          if (!annotationGroups.has(annotationId)) {
            const rect = element.getBoundingClientRect();
            annotationGroups.set(annotationId, {
              element,
              rect,
              annotationId,
              comment,
              top: rect.top,
              bottom: rect.bottom,
              middle: rect.top + (rect.bottom - rect.top) / 2,
            });
          }
        });
      });

      // Convert to array and sort by position
      const annotationsWithPositions = Array.from(annotationGroups.values())
        .sort((a, b) => a.top - b.top);

      annotationCacheRef.current = {
        elements: annotationsWithPositions,
        lastUpdate: Date.now(),
      };
    } catch (error) {
      console.error("Error rebuilding annotation cache:", error);
      annotationCacheRef.current = { elements: [], lastUpdate: Date.now() };
    }
  }, [DOMDocument, commentByAnnotationId]);

  // Optimized function to find current annotation
  const findCurrentAnnotation = useCallback(() => {
    if (!DOMDocument || !centerColumnRef?.current || !editorRef?.current || commentByAnnotationId.size === 0) {
      setCurrentAnnotation({ number: null, total: 0 });
      return;
    }

    try {
      // Cache iframe element reference
      if (!iframeElementRef.current) {
        iframeElementRef.current = window.document.getElementById("editorContainer");
      }

      const iframeElement = iframeElementRef.current;
      if (!iframeElement) {
        setCurrentAnnotation({ number: null, total: 0 });
        return;
      }

      // Use cached annotations or rebuild if needed
      let annotationsWithPositions = annotationCacheRef.current.elements;
      // Use reasonable cache timeout - not too short to avoid constant rebuilds
      const cacheTimeout = isLoading ? 2000 : 5000;
      if (annotationsWithPositions.length === 0 || Date.now() - annotationCacheRef.current.lastUpdate > cacheTimeout) {
        rebuildAnnotationCache();
        annotationsWithPositions = annotationCacheRef.current.elements;
      }

      if (annotationsWithPositions.length === 0) {
        setCurrentAnnotation({ number: null, total: 0 });
        return;
      }

      // Get viewport bounds (cached elements reduce getBoundingClientRect calls)
      const iframeRect = iframeElement.getBoundingClientRect();
      const centerColumnRect = centerColumnRef.current.getBoundingClientRect();

      const viewportTop = Math.max(centerColumnRect.top, iframeRect.top);
      const viewportBottom = Math.min(centerColumnRect.bottom, iframeRect.bottom);

      // Calculate sticky header offset and adjust viewport top
      const stickyHeaderOffset =
        isSticky && editorToolbarRef?.current
          ? editorToolbarRef.current.getBoundingClientRect().height + 75 // Match scrollToAnnotation offset
          : 0; // No offset when not sticky

      const adjustedViewportTop = viewportTop + stickyHeaderOffset;

      // Annotation rects are main-viewport coordinates — compare directly.
      const iframeViewportTop = adjustedViewportTop;
      const iframeViewportBottom = viewportBottom;

      if (iframeViewportTop >= iframeViewportBottom) {
        setCurrentAnnotation({ number: null, total: annotationsWithPositions.length });
        return;
      }

      // Find current annotation (use cached positions, only update rects when needed)
      let currentAnnotationIndex = null;
      let closestToTop = Infinity;

      // First pass: look for visible annotations and find the one closest to the top
      for (let i = 0; i < annotationsWithPositions.length; i++) {
        const annotation = annotationsWithPositions[i];
        // Get fresh rect only when checking visibility
        const rect = annotation.element.getBoundingClientRect();

        if (rect.bottom >= iframeViewportTop && rect.top <= iframeViewportBottom) {
          // Calculate distance from the top of the viewport
          const distanceFromTop = Math.abs(rect.top - iframeViewportTop);

          // Pick the annotation closest to the top of the viewport
          if (distanceFromTop < closestToTop) {
            closestToTop = distanceFromTop;
            currentAnnotationIndex = i;
          }
        }
      }

      // Second pass: find preceding annotation if none visible
      if (currentAnnotationIndex === null) {
        for (let i = annotationsWithPositions.length - 1; i >= 0; i--) {
          const annotation = annotationsWithPositions[i];
          const rect = annotation.element.getBoundingClientRect();

          if (rect.bottom < iframeViewportTop) {
            currentAnnotationIndex = i;
            break;
          }
        }
      }

      // Fallback: use first annotation if viewport is above all
      if (currentAnnotationIndex === null && annotationsWithPositions.length > 0) {
        const firstRect = annotationsWithPositions[0].element.getBoundingClientRect();
        if (firstRect.top > iframeViewportBottom) {
          currentAnnotationIndex = 0;
        }
      }

      const result = {
        number: currentAnnotationIndex !== null ? currentAnnotationIndex + 1 : null,
        total: annotationsWithPositions.length,
      };

      setCurrentAnnotation(result);
    } catch (error) {
      console.error("Error finding current annotation:", error);
      setCurrentAnnotation({ number: null, total: 0 });
    }
  }, [DOMDocument, centerColumnRef, editorRef, commentByAnnotationId, rebuildAnnotationCache, isSticky, editorToolbarRef]);

  // Consolidated cache management - handles both streaming and static updates
  useEffect(() => {
    annotationCacheRef.current = { elements: [], lastUpdate: 0 };

    // For non-loading states, just find current annotation without cache rebuild
    if (!isLoading) {
      findCurrentAnnotation();
      return;
    }

    // During loading, throttle cache rebuilds to prevent overlapping
    if (commentByAnnotationId.size > 0 && !isRebuildingRef.current) {
      isRebuildingRef.current = true;

      const timeoutId = setTimeout(() => {
        rebuildAnnotationCache();
        findCurrentAnnotation();
        isRebuildingRef.current = false;
      }, 300);

      return () => {
        clearTimeout(timeoutId);
        isRebuildingRef.current = false;
      };
    }
  }, [commentByAnnotationId, DOMDocument, isLoading, rebuildAnnotationCache, findCurrentAnnotation]);

  // Clear iframe cache when DOM document changes
  useEffect(() => {
    iframeElementRef.current = null; // Reset cache when DOM changes
  }, [DOMDocument]);

  // Expose the annotation ID at a given cache index (used by EditorContainer nav wrappers)
  const getAnnotationIdAtIndex = useCallback((index) => {
    const elements = annotationCacheRef.current.elements;
    if (index >= 0 && index < elements.length) {
      return elements[index].annotationId;
    }
    return null;
  }, []);

  // Scroll listener for finding current annotation
  useEffect(() => {
    let scrollTimeoutId;

    const debouncedFindAnnotation = () => {
      clearTimeout(scrollTimeoutId);
      // Use consistent debounce - too short interrupts cache rebuilds
      scrollTimeoutId = setTimeout(findCurrentAnnotation, 200);
    };

    const centerColumn = centerColumnRef?.current;
    if (centerColumn) {
      centerColumn.addEventListener("scroll", debouncedFindAnnotation, { passive: true });
      return () => {
        centerColumn.removeEventListener("scroll", debouncedFindAnnotation);
        clearTimeout(scrollTimeoutId);
      };
    }
  }, [centerColumnRef, findCurrentAnnotation]);

  return {
    currentAnnotation,
    goToCurrentComment,
    goToNextComment,
    goToPrevComment,
    scrollToAnnotation,
    scrollToAnnotationById,
    getAnnotationIdAtIndex
  };
};
